import React, { useRef, useEffect } from 'react';
import { CheckCircle, AlertCircle } from 'lucide-react';

const FormSidebar = ({ sections, activeSection, completionStatus, visibleSections, onSectionClick }) => {
  const filteredSections = sections.filter(s => visibleSections.includes(s.id));

  return (
    <>
      {/* Desktop sidebar */}
      <nav className="sticky top-6 w-56 shrink-0 hidden lg:block self-start">
        <div className="bg-light-bg-primary dark:bg-dark-bg-primary border border-light-border-primary dark:border-dark-border-primary rounded-xl p-2 shadow-sm">
          <div className="space-y-0.5">
            {filteredSections.map(section => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;
              const isComplete = completionStatus?.[section.id];
              // For 'conditional', check if section is actually required based on completion status
              // null means not applicable (don't show any indicator)
              const isRequired = section.required === true || (section.required === 'conditional' && isComplete !== null && !isComplete);

              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => onSectionClick(section.id)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium
                    transition-all duration-200 text-left group relative
                    ${isActive
                      ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                      : 'text-light-text-secondary dark:text-dark-text-secondary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary hover:text-light-text-primary dark:hover:text-dark-text-primary'
                    }`}
                >
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-0.5 h-5 bg-primary-500 rounded-full" />
                  )}
                  <Icon className={`h-4 w-4 shrink-0 transition-colors duration-200
                    ${isActive
                      ? 'text-primary-600 dark:text-primary-400'
                      : 'text-light-text-tertiary dark:text-dark-text-tertiary group-hover:text-light-text-secondary dark:group-hover:text-dark-text-secondary'
                    }`}
                  />
                  <span className="truncate flex-1">{section.label}</span>
                  {isComplete === null ? null : isComplete ? (
                    <CheckCircle className="h-3.5 w-3.5 text-green-500 dark:text-green-400 shrink-0" />
                  ) : isRequired ? (
                    <AlertCircle className="h-3.5 w-3.5 text-amber-500 dark:text-amber-400 shrink-0" />
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      </nav>
    </>
  );
};

const MobileFormNav = ({ sections, activeSection, completionStatus, visibleSections, onSectionClick }) => {
  const filteredSections = sections.filter(s => visibleSections.includes(s.id));
  const activeRef = useRef(null);
  const scrollRef = useRef(null);

  // Auto-scroll to active item in mobile nav
  useEffect(() => {
    if (activeRef.current && scrollRef.current) {
      const container = scrollRef.current;
      const item = activeRef.current;
      const containerRect = container.getBoundingClientRect();
      const itemRect = item.getBoundingClientRect();

      if (itemRect.left < containerRect.left || itemRect.right > containerRect.right) {
        item.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      }
    }
  }, [activeSection]);

  return (
    <div className="lg:hidden overflow-x-auto scrollbar-hide -mx-2 px-2 pb-3 mb-2">
      <div ref={scrollRef} className="flex gap-1.5 min-w-max">
        {filteredSections.map(section => {
          const Icon = section.icon;
          const isActive = activeSection === section.id;
          const isComplete = completionStatus?.[section.id];
          // For 'conditional', check if section is actually required based on completion status
          // null means not applicable (don't show any indicator)
          const isRequired = section.required === true || (section.required === 'conditional' && isComplete !== null && !isComplete);

          return (
            <button
              key={section.id}
              ref={isActive ? activeRef : null}
              type="button"
              onClick={() => onSectionClick(section.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium
                transition-all duration-200 whitespace-nowrap border
                ${isActive
                  ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400 border-primary-200 dark:border-primary-800'
                  : 'text-light-text-secondary dark:text-dark-text-secondary bg-light-bg-primary dark:bg-dark-bg-primary border-light-border-secondary dark:border-dark-border-secondary hover:bg-light-bg-secondary dark:hover:bg-dark-bg-secondary'
                }`}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              <span>{section.label}</span>
              {isComplete === null ? null : isComplete ? (
                <CheckCircle className="h-3 w-3 text-green-500 shrink-0" />
              ) : isRequired ? (
                <AlertCircle className="h-3 w-3 text-amber-500 shrink-0" />
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export { FormSidebar, MobileFormNav };
export default FormSidebar;
